"use client";

import React, { useState, useEffect, useRef } from "react";
import Image from "next/image";
import Slider from "react-slick";
// import "slick-carousel/slick/slick.css";
// import "slick-carousel/slick/slick-theme.css";
import "./css/Carousel.css"; // Custom styles for carousel
import ModalCarouselItem from "./ModalCarouselItem";
import parser from "html-react-parser";
import { getImageURL } from "@/helpers/imageURLHelper";

const Carousel = ({ key, slides, ntd = false }) => {
  const sliderRef = useRef(null);
  const [updateCount, setUpdateCount] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const startPos = useRef({ x: 0, y: 0 });
  const [openGalleryImage, setOpenGalleryImage] = useState(false);
  const [defaultImage, setDefaultImage] = useState(false);
  const [defaultIndex, setDefaultIndex] = useState(false);

  const addClassToItems = () => {
    const slides = document.querySelectorAll(".slick-slide");
    slides.forEach((slide) => {
      if (!slide.classList.contains("tps-slider-item")) {
        slide.classList.add("tps-slider-item");
        slide.setAttribute("aria-hidden", "false");
      }
    });
  };

  useEffect(() => {
    addClassToItems(); // Add class on initial render
  }, [updateCount]);

  if (!slides || slides.length === 0) {
    return null; // Return null if slides is undefined or empty
  }

  const handleMouseDown = (event) => {
    // Reset the drag state and capture the starting position
    setIsDragging(false);
    startPos.current = {
      x: event.clientX,
      y: event.clientY,
    };
  };

  const handleMouseMove = (event) => {
    // If the mouse moves more than a small threshold, it's a drag
    if (
      Math.abs(event.clientX - startPos.current.x) > 5 ||
      Math.abs(event.clientY - startPos.current.y) > 5
    ) {
      setIsDragging(true);
    }
  };

  const settings = {
    arrows: true,
    centerMode: true,
    centerPadding: 0,
    className: "tps-slider",
    dots: false,
    draggable: true,
    swipeToSlide: true,
    infinite: true,
    initialSlide: 1,
    lazyLoad: "progressive",
    slidesToScroll: 1,
    slidesToShow: 3,
    swipe: true,
    speed: 500,
    touchMove: true,
    touchThreshold: 100,
    beforeChange: () => setUpdateCount(updateCount + 1),
    afterChange: () => setUpdateCount(updateCount + 1),
    responsive: [
      {
        breakpoint: 768,
        settings: {
          centerPadding: 0,
          slidesToShow: 3,
          slidesToScroll: 1,
        },
      },
      {
        breakpoint: 520,
        settings: {
          centerPadding: 0,
          slidesToShow: 1,
          slidesToScroll: 1,
        },
      },
    ],
  };

  const handleCarouselOpenGalleryImage = (e, index) => {
    if (isDragging) {
      // If we're dragging, prevent the default click action.
      // This is crucial for avoiding a click when the user is trying to drag.
      e.preventDefault();
    } else {
      setOpenGalleryImage(!openGalleryImage);
      setDefaultImage(
        ntd ? slides[index] : slides[index].image.data.attributes
      );
      setDefaultIndex(index);
    }
  };

  const handleSlideChange = (e, direction) => {
    e.preventDefault();
    if (direction === "next") {
      if (defaultIndex < slides.length - 1) {
        setDefaultIndex(defaultIndex + 1);
        setDefaultImage(
          ntd
            ? slides[defaultIndex + 1]
            : slides[defaultIndex + 1].image.data?.attributes
        );
      } else {
        setDefaultIndex(0);
        setDefaultImage(ntd ? slides[0] : slides[0].image.data.attributes);
      }
    } else {
      if (defaultIndex == 0) {
        setDefaultIndex(slides.length - 1);
        setDefaultImage(
          ntd
            ? slides[slides.length - 1]
            : slides[slides.length - 1].image.data.attributes
        );
      } else {
        setDefaultIndex(defaultIndex - 1);
        setDefaultImage(
          ntd
            ? slides[defaultIndex - 1]
            : slides[defaultIndex - 1].image.data.attributes
        );
      }
    }
  };

  return (
    <div className="tps-slider-wrapper">
      <Slider {...settings} key={key} ref={sliderRef}>
        {slides?.map((item, index) => {
          const imageData = item?.image?.data?.attributes || item;
          const image = getImageURL(imageData?.url || imageData, ntd);

          return (
            <div
              key={index}
              onMouseDown={handleMouseDown}
              onMouseMove={handleMouseMove}
              onMouseUp={(e) => handleCarouselOpenGalleryImage(e, index)}
            >
              <a
                key={index}
                className="tps-slider-item-link tps-gallery-item"
                href="#"
                title={imageData?.alternativeText}
                data-gallery-array-id={`galleryItems_${index}`}
                data-item-id={`${imageData?.alternativeText}-${index}`}
                data-item-image={image}
                data-item-description=""
                data-item-link=""
                data-item-index={`galleryItems_${index}`}
                tabIndex="-1"
              >
                <Image
                  src={getImageURL(
                    imageData?.formats?.small?.url ||
                      imageData?.url ||
                      imageData,
                    ntd
                  )}
                  alt={imageData?.alternativeText || ""}
                  className="tps-slider-image"
                  width={
                    imageData?.formats?.small?.width || imageData?.width || 400
                  }
                  height={
                    imageData?.formats?.small?.height ||
                    imageData?.height ||
                    300
                  }
                />
                <span
                  className="tps-slider-image-bg"
                  data-bg={image}
                  style={{
                    backgroundPosition: "0% 0%",
                    backgroundImage: `url(${image})`,
                  }}
                ></span>
                {imageData?.caption && (
                  <p className="tps-slider-image-description">
                    <strong>
                      {imageData?.caption && parser(imageData?.caption)}
                    </strong>
                  </p>
                )}
              </a>
            </div>
          );
        })}
      </Slider>
      {openGalleryImage && (
        <ModalCarouselItem
          slide={defaultImage}
          index={defaultIndex}
          display={openGalleryImage}
          handleSlideChange={handleSlideChange}
          onClose={() => setOpenGalleryImage(false)}
          ntd={ntd}
        />
      )}
    </div>
  );
};

export default Carousel;
